import { useAxiosQueries } from 'hooks/useAxiosQueries';
import { combineDataAND } from 'hooks/useAxiosQueries/useAxiosQueries.utils';
import { useInfiniteAxiosQuery } from 'hooks/useInfiniteAxiosQuery';
import { useQueryParams } from 'hooks/useQueryParams';
import { useEffect, useMemo } from 'react';
import { Project } from 'types';

type UseGetOrganisationProjectsProps = {
  organisationId?: number;
  perPage?: number;
};

const splitParamsContainer = {
  keyParam: 'conjunction=or',
  splitParams: ['is_active', 'ts_start__gt', 'ts_end__lt'],
};

export const useGetOrganisationProjects = ({ organisationId, perPage = 12 }: UseGetOrganisationProjectsProps) => {
  const { queryKey, queryUrl, splitQueryUrls, paramsChanged, resetParamsChanged } = useQueryParams({
    key: ['organisation-projects', organisationId?.toString() ?? ''],
    baseUrl: `/organisations/${organisationId}/projects/`,
    splitParamsContainer,
  });

  const hasSingleQuery: boolean = splitQueryUrls.length === 0; // Only use the single query provided

  const {
    data: projects,
    isSuccess,
    isError,
    isLoading,
    isFetching,
    hasNextPage,
    goToNextPage,
    resetPage,
  } = useInfiniteAxiosQuery<Project>({
    key: queryKey,
    path: queryUrl,
    perPage,
    reactQueryConfig: { enabled: !!organisationId },
    accumulateData: true,
  });

  const { combinedData, isLoading: isAndOrLoading } = useAxiosQueries({
    queries: hasSingleQuery
      ? []
      : splitQueryUrls.map((url, index) => ({
          key: [...queryKey, `split-${index}`],
          path: `${url}&per_page=150`, //we want all results otherwise we can't AND/OR them properly
          config: { enabled: true },
          suspense: false,
        })),
    combineLogicFn: combineDataAND,
    uniqueEntityResolver: (project: Project) => project.projectId as number,
  });

  const finalProjects = useMemo(() => {
    if (hasSingleQuery) {
      return projects?.accumulatedData;
    }

    return combinedData;
  }, [hasSingleQuery, projects, combinedData]);

  useEffect(() => {
    if (paramsChanged) {
      resetPage();
      resetParamsChanged();
    }
  }, [paramsChanged, resetPage, resetParamsChanged]);

  return {
    projects: finalProjects,
    isSuccess,
    isError,
    isLoading: hasSingleQuery ? isLoading : isAndOrLoading,
    isFetching,
    hasNextPage: hasSingleQuery ? hasNextPage : false,
    goToNextPage: hasSingleQuery ? goToNextPage : undefined,
  };
};
